import { z } from 'zod';

import { DRAFT_ARTIFACT_CHAIN } from './artifacts.ts';

/**
 * Every job topic the prototype runs. All four are registered from the start even where
 * the consumer is still a stub: a queue that appears later has to be created while the
 * system is running, and both processes create queues at boot, so the cheap thing is to
 * declare the whole set now and fill in the handlers ticket by ticket.
 */
export const QUEUE_CONVERSATION_TURN = 'conversation.turn';
export const QUEUE_REMESH_READINESS_POLL = 'remesh.readiness-poll';
export const QUEUE_REMESH_PROGRESS_POLL = 'remesh.progress-poll';
export const QUEUE_SIMULATION_DRIVE = 'simulation.drive';

export const TURN_TRIGGERS = ['user', 'system'] as const;
export const turnTriggerSchema = z.enum(TURN_TRIGGERS);
export type TurnTrigger = z.infer<typeof turnTriggerSchema>;

/**
 * A turn is `running` until exactly one terminal event lands in its log, and the two
 * terminal statuses are the two terminal event kinds. Keeping the pair aligned is what
 * lets a reconnecting client trust the log alone: if the row says the turn ended, the
 * event that says so is already readable.
 */
export const TURN_STATUSES = ['running', 'done', 'error'] as const;
export type TurnStatus = (typeof TURN_STATUSES)[number];

export const TURN_EVENT_STATUS = 'status';
export const TURN_EVENT_TEXT = 'text';
export const TURN_EVENT_STOP_REQUESTED = 'stop_requested';
export const TURN_EVENT_DONE = 'done';
export const TURN_EVENT_ERROR = 'error';
/** A tool the agent decided to call, and what it returned. */
export const TURN_EVENT_TOOL_CALL = 'tool_call';
export const TURN_EVENT_TOOL_RESULT = 'tool_result';
/**
 * What one provider call cost. Emitted per step rather than once at the end so a turn that is
 * killed part-way has still reported the spend it incurred, and so a client can show a running
 * total instead of a number that only exists for turns that finished.
 */
export const TURN_EVENT_USAGE = 'usage';
/** A message the worker persisted, carrying the id the client needs to reconcile against. */
export const TURN_EVENT_MESSAGE = 'message';
/**
 * The provider reported a failure part-way through.
 *
 * Non-terminal, and always followed by a terminal `error`: this one carries what the provider
 * itself said, at the moment it said it, while the terminal event carries the code a client
 * branches on. A stream that failed still finishes cleanly at the transport level, so without
 * this the only trace of a rate limit or a context overflow would be a reply that stopped early.
 */
export const TURN_EVENT_PROVIDER_ERROR = 'provider_error';
/**
 * A cap was crossed by a turn that nonetheless finished what it was asked to do. Non-terminal on
 * purpose — the cap has already done its job by preventing the next call, and ending a complete
 * answer as an error would make the reload disagree with what the user just watched arrive.
 */
export const TURN_EVENT_SPEND_CAPPED = 'spend_capped';
/**
 * The two kinds that end a stream. The SSE endpoint closes when it forwards one, which is
 * only safe because the writer refuses to append anything after one — see
 * `appendTurnEvents`, where the same transaction that writes a terminal event moves the
 * turn row to a terminal status.
 */
export const TERMINAL_TURN_EVENT_KINDS = [TURN_EVENT_DONE, TURN_EVENT_ERROR] as const;
export type TerminalTurnEventKind = (typeof TERMINAL_TURN_EVENT_KINDS)[number];

export const isTerminalTurnEventKind = (kind: string): kind is TerminalTurnEventKind =>
  (TERMINAL_TURN_EVENT_KINDS as readonly string[]).includes(kind);

/** The turn status a terminal event implies, so the two can never be set independently. */
export const statusForTerminalKind = (kind: TerminalTurnEventKind): TurnStatus =>
  kind === TURN_EVENT_DONE ? 'done' : 'error';

/**
 * Event kinds are stored as text rather than an enum because the log is the extension
 * point: the agent runtime adds its own kinds, and a new kind
 * must not be a migration. The spine itself only has to recognise the terminal two, so
 * that is the only thing it validates a kind against.
 */
export const turnEventKindSchema = z.string().min(1).max(64);

/**
 * The terminal `error` payload. This is the one event shape the spine itself both writes and
 * reads — it puts the reason on the turn row — so it is the one that carries a schema here. The
 * kinds a later ticket introduces bring their own, next to the code that emits them.
 */
export const errorEventPayloadSchema = z.object({
  code: z.string().min(1),
  message: z.string().min(1),
});

/**
 * The payload shapes of the kinds the agent runtime emits.
 *
 * They live here, beside the kind constants, for one reason: the SPA renders these and must not
 * rediscover their shape by reading the worker. A kind whose payload is only defined at the point
 * it is written is a kind every consumer re-derives by hand and gets subtly wrong.
 *
 * The spine itself still does not parse them — it forwards payloads verbatim and never interprets
 * one — so these are a contract for consumers rather than a gate on the write path.
 */
export const providerErrorEventPayloadSchema = z.object({
  /**
   * Already redacted. The AI SDK replaces a provider's own words with a fixed string before they
   * reach the stream, and the worker keeps that: the real message can carry a prompt or a URL, so
   * it goes to the server log instead. Rendering this as diagnostics tells a user nothing.
   */
  message: z.string().min(1),
});

export const toolCallEventPayloadSchema = z.object({
  tool_call_id: z.string().min(1),
  tool_name: z.string().min(1),
  input: z.unknown(),
});

/**
 * A tool's outcome, discriminated explicitly rather than by which key is present.
 *
 * Both outcomes share one event kind, and telling them apart by looking for `output` would be
 * wrong the first time a tool legitimately returns `null` or `undefined` — the success case would
 * read as a failure. The `status` field makes the branch a fact the writer states.
 */
export const toolResultEventPayloadSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('ok'),
    tool_call_id: z.string().min(1),
    /**
     * Which tool this is the outcome of, repeated here rather than left to be looked up on the
     * matching call event. A consumer that acts on one tool's results has to survive being handed
     * the stream from a cursor — the SPA rebuilds its mapper on every reconnect and resumes after
     * the sequence it last saw, so the call event that named the tool may already be behind it.
     * Carrying the name makes a result event answerable on its own.
     *
     * **Optional**, because the log is append-only and older than this field. A row written before
     * it existed is a real row a client can be handed on any replay, and a required field would
     * fail the whole payload rather than the one thing it cannot say — turning a result nobody
     * needed the name of into an event that will not parse. A consumer keying on the name treats
     * its absence as "not my tool", which is what it does with every other name anyway.
     */
    tool_name: z.string().min(1).optional(),
    output: z.unknown(),
  }),
  z.object({
    status: z.literal('error'),
    tool_call_id: z.string().min(1),
    tool_name: z.string().min(1).optional(),
    error: z.string().min(1),
  }),
]);
export type ToolResultEventPayload = z.infer<typeof toolResultEventPayloadSchema>;

/**
 * What the SSE endpoint reads back out of the log. The payload stays `unknown` on purpose:
 * the spine forwards it verbatim and never interprets it, so validating it here would mean
 * this package having to know every kind any later ticket invents.
 */
export const turnEventSchema = z.object({
  seq: z.int().positive(),
  kind: turnEventKindSchema,
  payload: z.unknown(),
});
export type TurnEvent = z.infer<typeof turnEventSchema>;

/**
 * The reason a turn ended without finishing, as the terminal `error` payload carries it.
 * `interrupted` is the one the spine writes itself: a worker that died mid-turn leaves a
 * `running` row whose job is gone, and the successor closes the stream rather than leaving
 * a browser waiting on a turn nothing is executing.
 */
export const TURN_ERROR_INTERRUPTED = 'interrupted';
export const TURN_ERROR_STOPPED = 'stopped';
/**
 * The process was shutting down. Distinct from `stopped` because a deploy must not tell a user
 * they cancelled their own turn, and distinct from `interrupted` because this one was closed
 * cleanly on the way out rather than found abandoned afterwards.
 */
export const TURN_ERROR_SHUTTING_DOWN = 'shutting_down';
/**
 * Some of the turn's output could not be written. The reply the client holds is truncated, and
 * because sequence numbers are allocated at insert time the gap leaves no trace in the log — the
 * events that were lost never got a number, so what remains is densely numbered and looks whole.
 * Saying so in the terminal event is the only way a client can tell.
 */
export const TURN_ERROR_INCOMPLETE = 'incomplete';
/**
 * The loop used every iteration it was allowed and the model still wanted to call a tool, so the
 * reply is a truncated piece of work rather than a finished one. Distinct from a spend cap because
 * the fix is different: this one is a task that needs breaking up, not a budget that ran out.
 */
export const TURN_ERROR_ITERATION_CAP = 'iteration_cap_reached';
/**
 * The turn's row was written but its job could not be enqueued, so nothing was ever going to run
 * it. Closed immediately rather than left for the sweep, which would take the queue's own hour to
 * reach a turn that never emitted.
 */
export const TURN_ERROR_NOT_QUEUED = 'turn_not_queued';

/**
 * A conversation turn names the row the worker appends to and the study whose context it
 * assembles. Job payloads outlive the code that enqueued them, so this is parsed at the
 * point of use rather than trusted.
 */
export const conversationTurnPayloadSchema = z.object({
  turnId: z.uuid(),
  /**
   * The study this turn belongs to. It is the scope rather than a hint: the conversation, the
   * digest and everything the turn writes back are that study's, so a turn cannot be about a
   * study it does not name.
   */
  studyId: z.uuid(),
  /**
   * Present only on turns minted by the dev-only demo route, whose scripted sequence is what the
   * spine's own runtime verification streams. A real turn omits it and runs the agent.
   */
  demo: z
    .object({
      steps: z.int().positive().max(200).default(20),
      delayMs: z.int().nonnegative().max(10_000).default(250),
      /** Ends the turn with a terminal `error` instead of `done`. */
      failAtEnd: z.boolean().default(false),
      /**
       * Writes a scripted artifact into the turn's own study through the store, streaming it as
       * the agent's tool will.
       */
      writeArtifact: z.boolean().default(false),
      artifactKind: z.enum(DRAFT_ARTIFACT_CHAIN).default('rqs'),
    })
    .optional(),
});

export const remeshReadinessPollPayloadSchema = z.object({
  studyId: z.uuid(),
  /**
   * How many readiness polls of this study have already run — the whole of the timeout, and the
   * reason there is no second clock.
   *
   * Counting attempts from the first poll measures the wait from the moment fielding ended, which
   * is the only anchor this side is sure of: `endTime` is written by Remesh's real end path alone,
   * so a conversation ended by a status write carries none, and a deadline measured against it
   * would never come due for exactly the study most likely to be stuck. Carried on the job for the
   * same reason the progress chain carries its own: a count held in memory restarts when the
   * worker does, and this chain is meant to outlive that.
   */
  attempt: z.int().nonnegative().default(0),
});
export type RemeshReadinessPollPayload = z.infer<typeof remeshReadinessPollPayloadSchema>;
export const remeshProgressPollPayloadSchema = z.object({
  studyId: z.uuid(),
  /**
   * How many polls of this study have already run.
   *
   * Carried on the job rather than counted in the worker because the chain outlives any one
   * process: each poll enqueues the next, and a restart in between would reset a count held in
   * memory. Defaulted so the first job — the one the provisioning handler sends, which has no
   * predecessor to have counted — needs no knowledge of it.
   */
  attempt: z.int().nonnegative().default(0),
});
export type RemeshProgressPollPayload = z.infer<typeof remeshProgressPollPayloadSchema>;
export const simulationDrivePayloadSchema = z.object({ studyId: z.uuid() });
